import { FitAddon } from "@xterm/addon-fit";
import { SearchAddon } from "@xterm/addon-search";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { useEffect, useRef, useState } from "react";

import styles from "./TerminalsTab.module.css";
import { EmptyState } from "../../components/EmptyState";
import { ApiError, isSandboxUnavailable } from "../../data/api/client";
import {
  closeTerminal,
  createTerminal,
  type TerminalView,
  useTaskTerminals,
} from "../../data/api/terminals";
import { RealtimeClient } from "../../data/realtime/RealtimeClient";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";

// Initial PTY geometry for a freshly-created shell; the mounted xterm fits to
// the real pane size and issues a resize immediately after.
const DEFAULT_COLS = 80;
const DEFAULT_ROWS = 24;
const ENCODER = new TextEncoder();
const TERM_FONT =
  '"Geist Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';

// Highlight colours for scrollback search matches (terminals.md §Scrollback).
const SEARCH_DECORATIONS = {
  activeMatchBackground: "#7c3aed",
  activeMatchColorOverviewRuler: "#7c3aed",
  matchBackground: "#4b3f6b",
  matchOverviewRuler: "#4b3f6b",
} as const;

const dotClass = (status: TerminalView["status"]): string =>
  cx(
    styles.dot,
    status === "error" && styles.dotError,
    status === "open" && styles.dotOpen,
    status === "provisioning" && styles.dotProvisioning,
  );

const statusHint = (terminal: TerminalView): string | null => {
  if (terminal.status === "closed") {
    return terminal.exitCode === null
      ? "closed"
      : `exited ${terminal.exitCode}`;
  }
  if (terminal.status === "error") {
    return "error";
  }
  if (terminal.status === "provisioning") {
    return "opening…";
  }
  return null;
};

export type SearchState = {
  readonly dir: "next" | "prev";
  readonly query: string;
  // Bumped to re-trigger a find (next/prev) without changing the query.
  readonly tick: number;
};

type TerminalPaneProps = {
  readonly onResults: (r: { count: number; index: number }) => void;
  readonly search: SearchState | null;
  readonly taskId: string;
  readonly terminal: TerminalView;
};

// Mounts an xterm.js emulator bound to one PTY's realtime byte stream. Keyed by
// terminal id in the parent, so switching tabs remounts a clean emulator.
const TerminalPane = ({
  onResults,
  search,
  taskId,
  terminal,
}: TerminalPaneProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<SearchAddon | null>(null);
  // Keep the results callback in a ref so it doesn't retrigger the mount effect.
  const onResultsRef = useRef(onResults);
  useEffect(() => {
    onResultsRef.current = onResults;
  }, [onResults]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    let disposed = false;
    const term = new Terminal({
      cursorBlink: true,
      fontFamily: TERM_FONT,
      fontSize: 13,
      scrollback: 5000,
      theme: {
        background: "#0e0e10",
        cursor: "#7c3aed",
        foreground: "#e8e6e0",
      },
    });
    const fitAddon = new FitAddon();
    const searchAddon = new SearchAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(searchAddon);
    searchRef.current = searchAddon;
    const resultsSub = searchAddon.onDidChangeResults((r) =>
      onResultsRef.current({ count: r.resultCount, index: r.resultIndex }),
    );
    term.open(host);

    const fit = (): void => {
      try {
        fitAddon.fit();
      } catch {
        // Pane not measurable yet (hidden / zero-size) — skip this pass.
      }
    };
    fit();

    const handle = RealtimeClient.openTerminal(terminal.id, {
      onData: (bytes) => term.write(bytes),
    });
    handle.resize(term.cols, term.rows);
    const dataSub = term.onData((input) => handle.send(ENCODER.encode(input)));

    // xterm measures one character to size every cell, and it measures at
    // `open()` — which is normally before the webfont has arrived, since
    // fontsource serves it with `font-display: swap`. Measured against the
    // fallback, every cell is the wrong width and the glyphs drift out of
    // their columns. Re-measure once the real font is in.
    // `document.fonts` is absent under jsdom, and optional in older engines.
    void document.fonts?.ready.then(() => {
      if (disposed) {
        return;
      }
      term.clearTextureAtlas?.();
      fit();
      handle.resize(term.cols, term.rows);
    });

    const observer = new ResizeObserver(() => {
      fit();
      handle.resize(term.cols, term.rows);
    });
    observer.observe(host);
    term.focus();

    return () => {
      disposed = true;
      observer.disconnect();
      resultsSub.dispose();
      dataSub.dispose();
      handle.close();
      searchRef.current = null;
      term.dispose();
    };
  }, [taskId, terminal.id]);

  // Run the search when the query / tick / direction changes.
  useEffect(() => {
    const addon = searchRef.current;
    if (!addon) {
      return;
    }
    if (!search || search.query === "") {
      addon.clearDecorations();
      onResultsRef.current({ count: 0, index: -1 });
      return;
    }
    const options = { decorations: SEARCH_DECORATIONS };
    if (search.dir === "prev") {
      addon.findPrevious(search.query, options);
    } else {
      addon.findNext(search.query, options);
    }
  }, [search]);

  return <div className={styles.termHost} ref={hostRef} />;
};

export type TerminalsTabProps = {
  /** See `PreviewControls.onSandboxLost` — the same reread, from a refused shell. */
  readonly onSandboxLost: () => void;
  /**
   * This task's sandbox has been torn down — see `TaskDetail.sandboxReleased`.
   * Its shells are all closed and no new one can be spawned; the strip stays so
   * their archived last screens are still readable.
   */
  readonly sandboxReleased: boolean;
  readonly taskId: string;
};

export const TerminalsTab = ({
  onSandboxLost,
  sandboxReleased,
  taskId,
}: TerminalsTabProps) => {
  const {
    error,
    loading,
    refetch,
    terminals: fetched,
  } = useTaskTerminals(taskId);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Tabs the user has closed. A closed terminal keeps its row — its last screen
  // is archived and worth re-reading — so "closed" is a state the strip renders
  // and dismissal is what removes it (terminals.md §Tab lifecycle). Without
  // this, the "×" on an already-closed tab called DELETE, which matched no
  // live row, and the tab sat there refusing to go.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  const terminals = fetched.filter((t) => !dismissed.has(t.id));
  const [search, setSearch] = useState<SearchState | null>(null);
  const [results, setResults] = useState<{ count: number; index: number }>({
    count: 0,
    index: -1,
  });
  const searchInputRef = useRef<HTMLInputElement>(null);

  // A terminal opening / exiting anywhere on the task (any viewer) refetches the
  // strip, so provisioning→open and daemon-initiated exits show live.
  useRealtimeScope(`task:${taskId}`, (event) => {
    if (
      event.kind === "terminal.opened" ||
      event.kind === "terminal.exited" ||
      event.kind === "resync"
    ) {
      refetch();
    }
  });

  const closeSearch = (): void => {
    setSearch(null);
    setResults({ count: 0, index: -1 });
  };

  // ⌘F / Ctrl+F toggles scrollback search while the Terminals tab is mounted.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        setSearch((s) => (s ? null : { dir: "next", query: "", tick: 0 }));
        setResults({ count: 0, index: -1 });
        requestAnimationFrame(() => searchInputRef.current?.focus());
      } else if (
        e.key === "Escape" &&
        searchInputRef.current === document.activeElement
      ) {
        closeSearch();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const onNewShell = async (): Promise<void> => {
    setCreating(true);
    setCreateError(null);
    try {
      const { terminal } = await createTerminal(taskId, {
        cols: DEFAULT_COLS,
        rows: DEFAULT_ROWS,
      });
      setSelectedId(terminal.public_id);
      refetch();
    } catch (err) {
      setCreateError(
        err instanceof ApiError ? err.message : "Couldn't open a shell.",
      );
      // No shell is coming, here or ever: the button goes with the rest of the
      // sandbox's controls once the reread lands.
      if (isSandboxUnavailable(err)) {
        onSandboxLost();
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading && terminals.length === 0) {
    return (
      <div className={styles.wrap}>
        <div className={styles.state}>Loading terminals…</div>
      </div>
    );
  }
  if (error) {
    return (
      <EmptyState
        body="We couldn't reach the server to load this task's terminals."
        title="Something went sideways."
      />
    );
  }
  if (terminals.length === 0) {
    return (
      <div className={styles.wrap}>
        <div className={styles.empty}>
          <Icon.Terminal size={34} />
          <p className={styles.emptyTitle}>
            {sandboxReleased ? "No shells here" : "No shells yet — open one"}
          </p>
          <p className={styles.emptySub}>
            {sandboxReleased
              ? "This task’s sandbox is gone, so there is nothing left to run commands in."
              : "Run commands in the task’s sandbox."}
          </p>
          {!sandboxReleased ? (
            <button
              className={styles.newShellBtn}
              disabled={creating}
              onClick={() => void onNewShell()}
              type="button"
            >
              {creating ? "Opening…" : "+ new shell"}
            </button>
          ) : null}
          {createError ? (
            <p className={styles.emptyError}>{createError}</p>
          ) : null}
        </div>
      </div>
    );
  }

  const active = terminals.find((t) => t.id === selectedId) ?? terminals[0]!;
  const isLive = active.status === "open" || active.status === "provisioning";

  const onCloseTab = (id: string): void => {
    if (id === active.id) {
      // Move to the left neighbour, else the new first tab (terminals.md §Close).
      const idx = terminals.findIndex((t) => t.id === id);
      const rest = terminals.filter((t) => t.id !== id);
      setSelectedId((rest[idx - 1] ?? rest[0])?.id ?? null);
    }
    setDismissed((s) => new Set(s).add(id));
    // Still sent for an already-closed terminal: it is a no-op server-side, and
    // the alternative is the tab deciding which kind of close this is.
    void closeTerminal(id)
      .catch(() => undefined)
      .finally(() => refetch());
  };

  const runSearch = (query: string, dir: "next" | "prev"): void =>
    setSearch((s) => ({ dir, query, tick: (s?.tick ?? 0) + 1 }));

  return (
    <div className={styles.wrap}>
      <div className={styles.tabs}>
        {terminals.map((t) => {
          const hint = statusHint(t);
          return (
            <div
              aria-selected={t.id === active.id}
              className={cx(styles.tab, t.id === active.id && styles.active)}
              key={t.id}
              onClick={() => setSelectedId(t.id)}
              role="tab"
              tabIndex={-1}
            >
              <span className={dotClass(t.status)} />
              <span className={styles.tabName}>{t.name}</span>
              {hint ? <span className={styles.tabHint}>{hint}</span> : null}
              <span
                aria-label={`Close ${t.name}`}
                className={styles.x}
                onClick={(e) => {
                  e.stopPropagation();
                  onCloseTab(t.id);
                }}
                role="button"
                tabIndex={-1}
              >
                ×
              </span>
            </div>
          );
        })}
        {sandboxReleased ? (
          <span className={styles.released}>sandbox is gone</span>
        ) : (
          <button
            className={styles.new}
            disabled={creating}
            onClick={() => void onNewShell()}
            type="button"
          >
            {creating ? "opening…" : "+ new shell"}
          </button>
        )}
        <span className={styles.spacer} />
        <button
          aria-label="Search scrollback"
          className={styles.findBtn}
          onClick={() => {
            setSearch((s) => (s ? null : { dir: "next", query: "", tick: 0 }));
            requestAnimationFrame(() => searchInputRef.current?.focus());
          }}
          title="Find (⌘F)"
          type="button"
        >
          <Icon.Search size={13} />
        </button>
      </div>

      {search ? (
        <div className={styles.searchBar}>
          <Icon.Search size={13} />
          <input
            className={styles.searchInput}
            onChange={(e) => runSearch(e.target.value, "next")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                runSearch(search.query, e.shiftKey ? "prev" : "next");
              } else if (e.key === "Escape") {
                closeSearch();
              }
            }}
            placeholder="Find in scrollback…"
            ref={searchInputRef}
            value={search.query}
          />
          <span className={styles.searchCount}>
            {search.query === ""
              ? ""
              : results.count === 0
                ? "no matches"
                : `${results.index + 1} / ${results.count}`}
          </span>
          <button
            aria-label="Previous match"
            className={styles.searchNav}
            disabled={results.count === 0}
            onClick={() => runSearch(search.query, "prev")}
            type="button"
          >
            ↑
          </button>
          <button
            aria-label="Next match"
            className={styles.searchNav}
            disabled={results.count === 0}
            onClick={() => runSearch(search.query, "next")}
            type="button"
          >
            ↓
          </button>
          <button
            aria-label="Close search"
            className={styles.searchClose}
            onClick={closeSearch}
            type="button"
          >
            ×
          </button>
        </div>
      ) : null}

      {createError ? (
        <div className={styles.errorBar}>{createError}</div>
      ) : null}
      {isLive ? (
        <TerminalPane
          key={active.id}
          onResults={setResults}
          search={search}
          taskId={taskId}
          terminal={active}
        />
      ) : (
        <div className={styles.paneMsg}>
          <Icon.Terminal size={26} />
          <p className={styles.paneMsgTitle}>
            {active.status === "error"
              ? "This terminal hit an error."
              : active.exitCode === null
                ? "This shell has closed."
                : `Shell exited with code ${active.exitCode}.`}
          </p>
          <p className={styles.paneMsgSub}>
            {sandboxReleased
              ? "This task’s sandbox is gone — its shells are all closed."
              : "Open a new shell to keep working."}
          </p>
        </div>
      )}
    </div>
  );
};
